import React, { useState, useEffect, useRef } from "react";
import { Play, Pause } from "lucide-react";
import Header from './Header';

export default function VideoBluegecko() {
  // Intersection Observer to detect which section is in view
  const [visibleSections, setVisibleSections] = useState([false, false, false]);
  const sectionRefs = [useRef(null), useRef(null), useRef(null)];
  const [playing, setPlaying] = useState([false, false, false]);
  
  // Use a ref array for accessing video elements directly
  const videoRefs = useRef([]);

  // Content data for showcase sections
  const showcaseData = [
    {
      heading: "Gaming the advantage through your own data and Gen AI with BlueGecko",
      subheading: "Transform your data into strategic insights with AI",
      videoUrl: "/videos/bluegecko.mp4",
      position: "right",
      color: "blue"
    },
    {
      heading: "Falcon Mapping",
      subheading: "Make better business decisions with intelligent data analysis",
      videoUrl: "/videos/falcon.mp4",
      position: "left",
      color: "purple"
    },
    {
      heading: "Code Cheetah",
      subheading: "Tailored AI implementations for your specific industry needs",
      videoUrl: "/videos/cheetah.mp4",
      position: "right",
      color: "teal"
    },
  ];

  // Handle intersection observer entries
  const handleIntersectionChange = (entries) => {
    entries.forEach((entry) => {
      const index = sectionRefs.findIndex(
        (ref) => ref.current === entry.target
      );
      if (index !== -1) {
        setVisibleSections((prev) => {
          const newState = [...prev];
          newState[index] = entry.isIntersecting;
          return newState;
        });
      }
    });
  };

  // Setup and cleanup intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersectionChange, {
      threshold: 0.4,
    });

    sectionRefs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      sectionRefs.forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Improved video toggle function that directly interacts with the video element
  const toggleVideo = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play()
          .then(() => {
            updatePlayingState(index, true);
          })
          .catch((error) => {
            console.error("Error playing video:", error);
            updatePlayingState(index, false);
          });
      } else {
        video.pause();
        updatePlayingState(index, false);
      }
    }
  };

  // Helper function to update playing state
  const updatePlayingState = (index, isPlaying) => {
    setPlaying((prev) => {
      const updated = [...prev];
      updated[index] = isPlaying;
      return updated;
    });
  };

  // Scroll to next section function
  const scrollToSection = (index) => {
    sectionRefs[index].current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
                 <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
              <Header />

    <div className="bg-gradient-to-b from-gray-900 via-gray-800 to-blue-900 min-h-screen text-white overflow-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => {
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          return (
            <div
              key={`bg-element-${left.toFixed(2)}-${top.toFixed(2)}`}
              className="absolute rounded-full bg-blue-500"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                opacity: Math.random() * 0.1,
                filter: "blur(2px)",
                animation: `float ${Math.random() * 20 + 10}s linear infinite`,
              }}
            />
          );
        })}
      </div>

      {/* First section - Main Heading */}
      <div
        ref={sectionRefs[0]}
        className="min-h-screen flex items-center justify-center px-6 md:px-0"
      >
        <div
          className={`max-w-6xl mx-auto w-full transition-all duration-1000 
          ${visibleSections[0] ? "opacity-100" : "opacity-0 translate-y-10"}`}
        >
          <div className="flex flex-col md:flex-row items-center">
            {/* Left side - Text */}
            <div className="w-full md:w-1/2 pr-0 md:pr-12 mb-10 md:mb-0">
              <div className="h-1 w-24 bg-blue-500 mb-6"></div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent mb-6">
                Unlock Strategic Advantage Using Your Data and Generative AI
                with BlueGecko
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Unlock hidden value in your data with our cutting-edge
                generative AI solutions tailored for enterprise needs.
              </p>
              <div className="flex space-x-4">
                <button 
                  onClick={() => scrollToSection(1)}
                  className="border border-blue-400 text-blue-400 font-medium py-3 px-6 rounded-lg hover:bg-blue-900 hover:bg-opacity-30 transition-colors"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Right side - Video */}
            <div className="w-full md:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-blue-500/20 bg-gray-800">
                {/* Video container with 16:9 aspect ratio */}
                <div className="relative pt-[56.25%]">
                  <video
                    className="absolute inset-0 w-full h-full object-cover"
                    src={showcaseData[0].videoUrl}
                    loop
                    playsInline
                    ref={el => videoRefs.current[0] = el}
                  >
                    <track kind="captions" srcLang="en" label="English" />
                  </video>

                  {/* Video overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-6">
                    <div className="w-full">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Generative AI Solutions
                      </h3>
                      <p className="text-blue-200 text-sm mb-4">
                        See how BlueGecko transforms enterprise data
                      </p>

                      {/* Video controls */}
                      <button
                        onClick={() => toggleVideo(0)}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded-lg transition-colors"
                      >
                        {playing[0] ? (
                          <>
                            <Pause size={16} />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            <span>Play Video</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Glowing effect on hover */}
                {/* <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-gradient-to-r from-blue-600/20 to-purple-600/20 transition-opacity duration-300"></div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second section */}
      <div
        ref={sectionRefs[1]}
        className="min-h-screen flex items-center justify-center px-6 md:px-0"
      >
        <div
          className={`max-w-6xl mx-auto w-full transition-all duration-1000 
          ${visibleSections[1] ? "opacity-100" : "opacity-0 translate-y-10"}`}
        >
          <div className="flex flex-col md:flex-row-reverse items-center">
            {/* Right side (on desktop) - Text */}
            <div className="w-full md:w-1/2 pl-0 md:pl-12 mb-10 md:mb-0">
              <div className="h-1 w-24 bg-purple-500 mb-6"></div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-6">
                Falcon Mapping
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Transform complex enterprise data into actionable insights with
                our AI-powered analytics platform.
              </p>
              <div className="flex space-x-4">
                {/* <button
                  onClick={() => scrollToSection(2)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium py-3 px-6 rounded-lg transform transition-transform hover:scale-105"
                >
                  See Analytics
                </button> */}
              </div>
            </div>

            {/* Left side (on desktop) - Video */}
            <div className="w-full md:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-purple-500/20 bg-gray-800">
                {/* Video container with 16:9 aspect ratio */}
                <div className="relative pt-[56.25%]">
                  <video
                    className="absolute inset-0 w-full h-full object-cover"
                    src={showcaseData[1].videoUrl}
                    loop
                    playsInline
                    ref={el => videoRefs.current[1] = el}
                  >
                    <track kind="captions" srcLang="en" label="English" />
                  </video>

                  {/* Video overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-6">
                    <div className="w-full">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Decision Intelligence
                      </h3>
                      <p className="text-purple-200 text-sm mb-4">
                        AI-powered analytics for better business decisions
                      </p>

                      {/* Video controls */}
                      <button
                        onClick={() => toggleVideo(1)}
                        className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-500 text-white py-2 px-4 rounded-lg transition-colors"
                      >
                        {playing[1] ? (
                          <>
                            <Pause size={16} />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            <span>Play Video</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Glowing effect on hover */}
                {/* <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-gradient-to-r from-purple-600/20 to-pink-600/20 transition-opacity duration-300"></div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Third section */}
      <div
        ref={sectionRefs[2]}
        className="min-h-screen flex items-center justify-center px-6 md:px-0"
      >
        <div
          className={`max-w-6xl mx-auto w-full transition-all duration-1000 
          ${visibleSections[2] ? "opacity-100" : "opacity-0 translate-y-10"}`}
        >
          <div className="flex flex-col md:flex-row items-center">
            {/* Left side - Text */}
            <div className="w-full md:w-1/2 pr-0 md:pr-12 mb-10 md:mb-0">
              <div className="h-1 w-24 bg-teal-500 mb-6"></div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent mb-6">
                Code Cheetah
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Tailored AI implementations designed specifically for your
                industry and business challenges.
              </p>
              <div className="flex space-x-4">
                {/* <button className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-500 hover:to-green-500 text-white font-medium py-3 px-6 rounded-lg transform transition-transform hover:scale-105">
                  Get Started
                </button> */}
              </div>
            </div>

            {/* Right side - Video */}
            <div className="w-full md:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-teal-500/20 bg-gray-800">
                {/* Video container with 16:9 aspect ratio */}
                <div className="relative pt-[56.25%]">
                  <video
                    className="absolute inset-0 w-full h-full object-cover"
                    src={showcaseData[2].videoUrl}
                    loop
                    playsInline
                    ref={el => videoRefs.current[2] = el}
                  >
                    <track kind="captions" srcLang="en" label="English" />
                  </video>

                  {/* Video overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-6">
                    <div className="w-full">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Industry-Specific Solutions
                      </h3>
                      <p className="text-teal-200 text-sm mb-4">
                        Custom AI solutions for your business needs
                      </p>

                      {/* Video controls */}
                      <button
                        onClick={() => toggleVideo(2)}
                        className="flex items-center space-x-2 bg-teal-600 hover:bg-teal-500 text-white py-2 px-4 rounded-lg transition-colors"
                      >
                        {playing[2] ? (
                          <>
                            <Pause size={16} />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            <span>Play Video</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Glowing effect on hover */}
                {/* <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-gradient-to-r from-teal-600/20 to-green-600/20 transition-opacity duration-300"></div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator (fixed position) */}
      <div className="fixed bottom-10 right-10 flex flex-col items-center opacity-70 hover:opacity-100 transition-opacity cursor-pointer">
        <span className="text-xs font-medium text-blue-300 mb-2">
          Scroll to explore
        </span>
        <div className="w-8 h-14 border-2 border-blue-400 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-blue-400 rounded-full mt-2 animate-bounce"></div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
    </div>

  );
}